/**
 * A/B/C/D control-scheme experiment — registry, copy, and the kill switch.
 *
 * WHY THIS LIVES IN ITS OWN FOLDER
 * --------------------------------
 * This is a PARALLEL layer, not a rewrite. The shipped viewer keeps working
 * exactly as it does today; the experiment sits on top of it so we can put
 * four candidate control schemes in front of real people, collect opinions,
 * then promote the winner to default and delete the rest.
 *
 * Turning it off is one line: `DEFAULT_MODE = 'off'` below. Removing it
 * entirely is `rm -r src/experiments/` plus the single guarded block in
 * mount.ts (search: "A/B/C/D control-scheme experiment"). Nothing else in the
 * viewer imports from here, and no existing file's behaviour changes while the
 * mode is off — the picker's own styles are injected at runtime rather than
 * added to viewer.css, so there is no shared file to clean up afterwards.
 *
 * Per-link override, so one deploy serves every world:
 *     ?ab=0     the shipped viewer, no cog (clean demo mid-experiment)
 *     ?ab=1     the blocking picker on load (testing)
 *     ?ab=cog   the shipped start with the cog in the toolbar
 * In an iframe embed this reads the VIEWER's url (the iframe src), which the
 * marketing site controls — same contract as ?lang= (the homepage modal
 * forwards ?ab= from its own url for exactly this reason).
 *
 * Four registries, all live: scene kind (walkable / rotating) × device
 * (desktop / touch), each with its own lettered list — see FOUR REGISTRIES.
 */

import type {
    GyroOrbitConfig, KeyOrbitConfig, OrbitController, PointerOrbitConfig,
    ScrollOrbitConfig, ZoomHoldConfig
} from '../controls/orbit-controller';
import type {
    EdgeLookConfig, GyroLookConfig, PointerLookConfig, TwoFingerMoveConfig, WalkController
} from '../controls/walk-controller';
import type { HintRow } from '../ui/controls-hint';
import { resolveLang, type UiLang } from '../ui/lang';

export type SchemeMode = 'off' | 'prompt' | 'cog';

/**
 * What a plain link (no ?ab=) gets:
 *   'cog'    the viewer starts exactly as shipped (scheme A) with the cog in
 *            the toolbar to switch — for the viewer-schemes branch / previews.
 *   'prompt' the blocking picker on load — the testing setting.
 *   'off'    the shipped viewer, not a trace of this folder.
 *
 * On main this MUST stay 'off': main is what prod deploys from, and the
 * experiment shipped to prod on 26 Aug 2026 because it was 'cog' here. Turn it
 * on per link (?ab=cog / ?ab=1) or on the viewer-schemes branch, never here.
 */
const DEFAULT_MODE: SchemeMode = 'off';

export type SchemeId = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

/** Fixed order — it is the order of the tiles AND the label people will quote
 *  back at us ("I liked C"), so it must not depend on object key order. */
export const SCHEME_IDS: readonly SchemeId[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;

/** The four registries: scene kind × device. See FOUR REGISTRIES below. */
export type Registry = 'walk' | 'orbit' | 'walk-touch' | 'orbit-touch';

export function registryFor(kind: 'walk' | 'orbit', touch: boolean): Registry {
    return touch ? (kind === 'walk' ? 'walk-touch' : 'orbit-touch') : kind;
}

/** Which of the letters each registry actually offers. */
const REGISTRY_SCHEMES: Record<Registry, readonly SchemeId[]> = {
    walk: ['A', 'B', 'C', 'D', 'E'],
    orbit: ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
    'walk-touch': ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
    'orbit-touch': ['A', 'B', 'C']
};

export function schemeIdsFor(registry: Registry): readonly SchemeId[] {
    return REGISTRY_SCHEMES[registry];
}

/** Which scheme is pre-highlighted when nobody has chosen yet. 'A' is today's
 *  shipped behaviour — the control arm of the test. */
export const DEFAULT_SCHEME: SchemeId = 'A';

export function schemeMode(): SchemeMode {
    try {
        const q = new URLSearchParams(window.location.search).get('ab');
        if (q === '0' || q === 'off') return 'off';
        if (q === '1' || q === 'on' || q === 'prompt') return 'prompt';
        if (q === 'cog') return 'cog';
    } catch { /* no URL access (worker/test) — fall through */ }
    return DEFAULT_MODE;
}

/** Touch-first device: the primary pointer is a finger. One definition for
 *  the gate, the picker's copy and the tutorial rows, so they cannot drift. */
export function isTouchDevice(): boolean {
    try {
        return matchMedia('(pointer: coarse)').matches;
    } catch {
        return false;
    }
}

/**
 * All four registries exist now, so every walkable or rotating scene on any
 * device gets the layer. Kept as the one place a future gate would go.
 */
export function schemesEligible(kind: 'walk' | 'orbit'): boolean {
    void kind;
    return true;
}

// ── Persistence ───────────────────────────────────────────────────────────
// Deliberately NOT "seen the picker, never show it again": the whole point of
// the experiment is that everyone meets the menu, so the panel blocks on every
// load. What we remember is only the LAST PICK, used to pre-highlight a tile so
// a returning tester lands on their favourite. localStorage is wrapped because
// a third-party iframe gets no storage at all under Safari's partitioning —
// that must degrade to "no preference", never to a crash.
const STORAGE_KEY = 'ollo.controlScheme';

export function savedScheme(): SchemeId | null {
    try {
        const v = window.localStorage.getItem(STORAGE_KEY);
        return SCHEME_IDS.includes(v as SchemeId) ? (v as SchemeId) : null;
    } catch {
        return null;
    }
}

export function saveScheme(id: SchemeId): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, id);
    } catch { /* private mode / partitioned iframe — preference is just lost */ }
}

// ── Copy ──────────────────────────────────────────────────────────────────
// Device-split descriptions: "joystick to move" is nonsense on a laptop and
// "scroll" is nonsense on a phone, and this panel's entire job is to let
// someone predict how a scheme will feel BEFORE they commit to it.
export interface SchemeCopy {
    /** Short device-neutral name shown next to the A/B/C/D badge. */
    name: string;
    /** One line, ~40 chars: what the scheme does with a mouse / with a finger. */
    mouse: string;
    touch: string;
    /** Optional dimmer third line. A scheme defined by what it REMOVES can't
     *  say so in the same breath as what it does — B needs both lines. */
    note?: string;
    /** Tutorial-card rows, for a scheme the shipped card's copy would lie
     *  about (C has no drag to teach). Desktop only. */
    hint?: { look: string; move: string };
}

export interface FeedbackCopy {
    /** The button under the schemes that opens the feedback step. */
    open: string;
    title: string;
    sub: string;
    /** A mode this tester hasn't entered yet — the row switches to it. */
    notTried: string;
    favourite: string;
    comment: string;
    name: string;
    send: string;
    sending: string;
    back: string;
    thanks: string;
    thanksSub: string;
    done: string;
    error: string;
}

export interface PickerCopy {
    title: string;
    /** Per registry: it counts the tiles ("Five ways…" / "Seven ways…"). */
    subtitle: Record<Registry, string>;
    /** Cog button label — how you get back here. */
    change: string;
    close: string;
    /** Label of the panel's link back to its language step. */
    language: string;
    /** The ~5 s callout under the cog after the first pick. */
    cogHint: string;
    /** The feedback step: stars for the modes you tried, ♥ a favourite, a
     *  comment, an optional name. */
    feedback: FeedbackCopy;
    /** Per registry: schemes sharing a letter across registries are different
     *  experiments. Partial because registries don't offer every letter —
     *  REGISTRY_SCHEMES is the source of truth. Touch registries fill
     *  `touch` and leave `mouse` empty; desktop ones the reverse. */
    schemes: Record<Registry, Partial<Record<SchemeId, SchemeCopy>>>;
}

export const COPY: Record<UiLang, PickerCopy> = {
    en: {
        title: 'How do you want to move?',
        subtitle: {
            walk: 'Five ways to explore. Pick one — you can switch any time.',
            orbit: 'Seven ways to explore. Pick one — you can switch any time.',
            'walk-touch': 'Seven ways to explore. Pick one — you can switch any time.',
            'orbit-touch': 'Three ways to explore. Pick one — you can switch any time.'
        },
        change: 'Change controls',
        close: 'Close',
        language: 'Language',
        cogHint: 'Back to the viewing menu here',
        feedback: {
            open: 'Give feedback',
            title: 'How did it feel?',
            sub: 'Rate the modes you tried and tap ♥ on your favourite. Everything is optional.',
            notTried: 'Not tried yet — tap to try it',
            favourite: 'Favourite',
            comment: 'What did you like or dislike? (optional)',
            name: 'Your name (optional)',
            send: 'Send',
            sending: 'Sending…',
            back: 'Back to modes',
            thanks: 'Thank you — that really helps!',
            thanksSub: 'Keep exploring if you like. If you change your mind, send it again.',
            done: 'Back to the scene',
            error: 'Couldn’t send. Check your connection and try again.'
        },
        schemes: {
            walk: {
                A: { name: 'Classic',      mouse: 'Drag to look, scroll or WASD to move', touch: 'Swipe to look, joystick to move' },
                B: { name: 'Classic lite', mouse: 'Drag to look, scroll or WASD to move', touch: '',
                     note: 'No joystick, no click-to-move · Shift + WASD to go faster' },
                C: { name: 'Cursor',       mouse: 'Cursor to the edge to look, WASD to move', touch: '',
                     note: 'No click, no drag · Shift + WASD to go faster',
                     hint: { look: 'Cursor to the edge to look around', move: 'WASD to move' } },
                D: { name: 'Hybrid',       mouse: 'Drag or cursor to look, scroll or WASD to move', touch: '',
                     note: 'Classic lite + Cursor in one · Shift + WASD to go faster',
                     hint: { look: 'Drag or cursor to the edge to look', move: 'WASD or scroll to move' } },
                E: { name: 'Gamer',        mouse: 'Click, then mouse to look, WASD to move', touch: '',
                     note: 'Esc gives the cursor back · Shift + WASD to go faster',
                     hint: { look: 'Click, then move the mouse to look', move: 'WASD to move' } }
            },
            orbit: {
                A: { name: 'Classic', mouse: 'Drag to rotate, scroll to zoom', touch: 'Swipe to rotate, pinch to zoom' },
                B: { name: 'WASD',    mouse: 'WASD to look around, scroll to zoom', touch: '',
                     note: 'A/D turn, W/S tilt · drag still works',
                     hint: { look: 'WASD to look around', move: 'Scroll or pinch to zoom' } },
                C: { name: 'Cursor',  mouse: 'Cursor to the edge to turn, scroll to zoom', touch: '',
                     note: 'No drag — the margins steer',
                     hint: { look: 'Cursor to the edge to turn', move: 'Scroll or pinch to zoom' } },
                D: { name: 'Cursor & keys', mouse: 'Cursor to the edge to turn, W/S to zoom', touch: '',
                     note: 'No drag, no scroll · W zooms in, S out',
                     hint: { look: 'Cursor to the edge to turn', move: 'W/S to zoom' } },
                E: { name: 'Spacebar', mouse: 'WASD to look around, Space to zoom', touch: '',
                     note: 'Hold Space to zoom · each press flips in/out · drag still works',
                     hint: { look: 'WASD to look around', move: 'Hold Space to zoom — a press flips in/out' } },
                F: { name: 'Scroll',   mouse: 'Scroll to look around, pinch or W/S to zoom', touch: '',
                     note: 'Two fingers steer · drag still works',
                     hint: { look: 'Scroll to look around', move: 'Pinch or W/S to zoom' } },
                G: { name: 'Gamer',    mouse: 'Click, then mouse to orbit, W/S to zoom', touch: '',
                     note: 'Esc gives the cursor back',
                     hint: { look: 'Click, then move the mouse to orbit', move: 'W/S to zoom' } }
            },
            'walk-touch': {
                A: { name: 'Classic',     mouse: '', touch: 'Swipe to look, joystick to move' },
                B: { name: 'Gyro',        mouse: '', touch: 'Turn the device to look, joystick to move',
                     note: 'Allow motion access when asked · double-tap to recenter',
                     hint: { look: 'Turn the device to look around', move: 'Joystick to move' } },
                C: { name: 'Hold',        mouse: '', touch: 'Swipe to look, hold to walk',
                     note: 'No joystick · touch and hold anywhere to walk forward',
                     hint: { look: 'Swipe to look around', move: 'Touch and hold to walk' } },
                D: { name: 'Two fingers', mouse: '', touch: 'Swipe to look, two fingers to move',
                     note: 'Slide two fingers up to walk forward, down to back up',
                     hint: { look: 'Swipe to look around', move: 'Two fingers up or down to move' } },
                E: { name: 'Thumbs',      mouse: '', touch: 'Left thumb moves, right thumb looks',
                     note: 'The joystick appears wherever your left thumb lands',
                     hint: { look: 'Right half: swipe to look', move: 'Left half: drag to move' } },
                F: { name: 'Gyro + hold', mouse: '', touch: 'Turn the device to look, hold to walk',
                     note: 'One-handed · allow motion access when asked · double-tap to recenter',
                     hint: { look: 'Turn the device to look around', move: 'Touch and hold to walk' } },
                G: { name: 'Hold the edge', mouse: '', touch: 'Hold the edge to look, joystick to move',
                     note: 'Sides turn, top and bottom tilt · no swipe',
                     hint: { look: 'Hold a finger at the edge to look around', move: 'Joystick to move' } }
            },
            'orbit-touch': {
                A: { name: 'Classic',       mouse: '', touch: 'Swipe to rotate, pinch to zoom' },
                B: { name: 'Gyro',          mouse: '', touch: 'Turn the device to orbit, pinch to zoom',
                     note: 'Like holding a model · allow motion access when asked · double-tap to recenter',
                     hint: { look: 'Turn the device to orbit', move: 'Pinch to zoom' } },
                C: { name: 'Hold the edge', mouse: '', touch: 'Hold the edge to orbit, pinch to zoom',
                     note: 'Rest a finger in a margin · swipe still rotates',
                     hint: { look: 'Hold a finger at the edge to orbit', move: 'Pinch to zoom' } }
            }
        }
    },
    es: {
        title: '¿Cómo quieres moverte?',
        subtitle: {
            walk: 'Cinco formas de explorar. Elige una — puedes cambiarla cuando quieras.',
            orbit: 'Siete formas de explorar. Elige una — puedes cambiarla cuando quieras.',
            'walk-touch': 'Siete formas de explorar. Elige una — puedes cambiarla cuando quieras.',
            'orbit-touch': 'Tres formas de explorar. Elige una — puedes cambiarla cuando quieras.'
        },
        change: 'Cambiar controles',
        close: 'Cerrar',
        language: 'Idioma',
        cogHint: 'Aquí vuelves al menú de visualización',
        feedback: {
            open: 'Danos tu opinión',
            title: '¿Qué te ha parecido?',
            sub: 'Puntúa los modos que has probado y toca ♥ en tu favorito. Todo es opcional.',
            notTried: 'Aún sin probar — toca para probarlo',
            favourite: 'Favorito',
            comment: '¿Qué te ha gustado y qué no? (opcional)',
            name: 'Tu nombre (opcional)',
            send: 'Enviar',
            sending: 'Enviando…',
            back: 'Volver a los modos',
            thanks: '¡Gracias, nos ayuda mucho!',
            thanksSub: 'Sigue explorando si quieres. Si cambias de opinión, vuelve a enviarla.',
            done: 'Volver a la escena',
            error: 'No se ha podido enviar. Revisa la conexión y vuelve a intentarlo.'
        },
        schemes: {
            walk: {
                A: { name: 'Clásico',      mouse: 'Arrastra para mirar, scroll o WASD para moverte', touch: 'Desliza para mirar, joystick para moverte' },
                B: { name: 'Clásico lite', mouse: 'Arrastra para mirar, scroll o WASD para moverte', touch: '',
                     note: 'Sin joystick ni clic para moverte · Shift + WASD acelera' },
                C: { name: 'Cursor',       mouse: 'Cursor al borde para mirar, WASD para moverte', touch: '',
                     note: 'Sin clic ni arrastre · Shift + WASD acelera',
                     hint: { look: 'Cursor al borde para mirar', move: 'WASD para moverte' } },
                D: { name: 'Híbrido',      mouse: 'Arrastra o cursor para mirar, WASD para moverte', touch: '',
                     note: 'Clásico lite + Cursor a la vez · Shift + WASD acelera',
                     hint: { look: 'Arrastra o cursor al borde para mirar', move: 'WASD o scroll para moverte' } },
                E: { name: 'Gamer',        mouse: 'Clic y ratón para mirar, WASD para moverte', touch: '',
                     note: 'Esc devuelve el cursor · Shift + WASD acelera',
                     hint: { look: 'Haz clic y mueve el ratón para mirar', move: 'WASD para moverte' } }
            },
            orbit: {
                A: { name: 'Clásico', mouse: 'Arrastra para girar, scroll para acercar', touch: 'Desliza para girar, pellizca para acercar' },
                B: { name: 'WASD',    mouse: 'WASD para mirar, scroll para acercar', touch: '',
                     note: 'A/D giran, W/S inclinan · arrastrar sigue funcionando',
                     hint: { look: 'WASD para mirar', move: 'Scroll o pellizca para acercar' } },
                C: { name: 'Cursor',  mouse: 'Cursor al borde para girar, scroll para acercar', touch: '',
                     note: 'Sin arrastre — los márgenes dirigen',
                     hint: { look: 'Cursor al borde para girar', move: 'Scroll o pellizca para acercar' } },
                D: { name: 'Cursor y teclas', mouse: 'Cursor al borde para girar, W/S para acercar', touch: '',
                     note: 'Sin arrastre ni scroll · W acerca, S aleja',
                     hint: { look: 'Cursor al borde para girar', move: 'W/S para acercar' } },
                E: { name: 'Espacio', mouse: 'WASD para mirar, Espacio para acercar', touch: '',
                     note: 'Mantén Espacio para zoom · cada pulsación invierte · arrastrar sigue funcionando',
                     hint: { look: 'WASD para mirar', move: 'Mantén Espacio para zoom — pulsar invierte' } },
                F: { name: 'Scroll',  mouse: 'Scroll para mirar, pellizca o W/S para acercar', touch: '',
                     note: 'Dos dedos dirigen · arrastrar sigue funcionando',
                     hint: { look: 'Scroll para mirar', move: 'Pellizca o W/S para acercar' } },
                G: { name: 'Gamer',   mouse: 'Clic y ratón para girar, W/S para acercar', touch: '',
                     note: 'Esc devuelve el cursor',
                     hint: { look: 'Haz clic y mueve el ratón para girar', move: 'W/S para acercar' } }
            },
            'walk-touch': {
                A: { name: 'Clásico',     mouse: '', touch: 'Desliza para mirar, joystick para moverte' },
                B: { name: 'Giroscopio',  mouse: '', touch: 'Gira el dispositivo para mirar, joystick para moverte',
                     note: 'Permite el acceso al movimiento cuando se pida · doble toque para recentrar',
                     hint: { look: 'Gira el dispositivo para mirar', move: 'Joystick para moverte' } },
                C: { name: 'Mantener',    mouse: '', touch: 'Desliza para mirar, mantén para andar',
                     note: 'Sin joystick · mantén el dedo en cualquier sitio para avanzar',
                     hint: { look: 'Desliza para mirar', move: 'Mantén el dedo para andar' } },
                D: { name: 'Dos dedos',   mouse: '', touch: 'Desliza para mirar, dos dedos para moverte',
                     note: 'Desliza dos dedos hacia arriba para avanzar, hacia abajo para retroceder',
                     hint: { look: 'Desliza para mirar', move: 'Dos dedos arriba o abajo para moverte' } },
                E: { name: 'Pulgares',    mouse: '', touch: 'Pulgar izquierdo mueve, pulgar derecho mira',
                     note: 'El joystick aparece donde pongas el pulgar izquierdo',
                     hint: { look: 'Mitad derecha: desliza para mirar', move: 'Mitad izquierda: arrastra para moverte' } },
                F: { name: 'Giro + mantener', mouse: '', touch: 'Gira el dispositivo para mirar, mantén para andar',
                     note: 'Con una mano · permite el acceso al movimiento cuando se pida · doble toque para recentrar',
                     hint: { look: 'Gira el dispositivo para mirar', move: 'Mantén el dedo para andar' } },
                G: { name: 'Mantén el borde', mouse: '', touch: 'Mantén el borde para mirar, joystick para moverte',
                     note: 'Los lados giran, arriba y abajo inclinan · sin deslizar',
                     hint: { look: 'Mantén un dedo en el borde para mirar', move: 'Joystick para moverte' } }
            },
            'orbit-touch': {
                A: { name: 'Clásico',       mouse: '', touch: 'Desliza para girar, pellizca para acercar' },
                B: { name: 'Giroscopio',    mouse: '', touch: 'Gira el dispositivo para orbitar, pellizca para acercar',
                     note: 'Como sostener una maqueta · permite el acceso al movimiento cuando se pida · doble toque para recentrar',
                     hint: { look: 'Gira el dispositivo para orbitar', move: 'Pellizca para acercar' } },
                C: { name: 'Mantén el borde', mouse: '', touch: 'Mantén el borde para orbitar, pellizca para acercar',
                     note: 'Apoya un dedo en el margen · deslizar sigue girando',
                     hint: { look: 'Mantén un dedo en el borde para orbitar', move: 'Pellizca para acercar' } }
            }
        }
    },
    ca: {
        title: 'Com vols moure’t?',
        subtitle: {
            walk: 'Cinc maneres d’explorar. Tria’n una — la pots canviar quan vulguis.',
            orbit: 'Set maneres d’explorar. Tria’n una — la pots canviar quan vulguis.',
            'walk-touch': 'Set maneres d’explorar. Tria’n una — la pots canviar quan vulguis.',
            'orbit-touch': 'Tres maneres d’explorar. Tria’n una — la pots canviar quan vulguis.'
        },
        change: 'Canviar controls',
        close: 'Tancar',
        language: 'Idioma',
        cogHint: 'Aquí tornes al menú de visualització',
        feedback: {
            open: 'Dona’ns la teva opinió',
            title: 'Què t’ha semblat?',
            sub: 'Puntua els modes que has provat i toca ♥ al teu preferit. Tot és opcional.',
            notTried: 'Encara sense provar — toca per provar-lo',
            favourite: 'Preferit',
            comment: 'Què t’ha agradat i què no? (opcional)',
            name: 'El teu nom (opcional)',
            send: 'Envia',
            sending: 'Enviant…',
            back: 'Torna als modes',
            thanks: 'Gràcies, ens ajuda molt!',
            thanksSub: 'Continua explorant si vols. Si canvies d’opinió, torna-la a enviar.',
            done: 'Torna a l’escena',
            error: 'No s’ha pogut enviar. Revisa la connexió i torna-ho a provar.'
        },
        schemes: {
            walk: {
                A: { name: 'Clàssic',      mouse: 'Arrossega per mirar, scroll o WASD per moure’t', touch: 'Llisca per mirar, joystick per moure’t' },
                B: { name: 'Clàssic lite', mouse: 'Arrossega per mirar, scroll o WASD per moure’t', touch: '',
                     note: 'Sense joystick ni clic per moure’t · Shift + WASD accelera' },
                C: { name: 'Cursor',       mouse: 'Cursor a la vora per mirar, WASD per moure’t', touch: '',
                     note: 'Sense clic ni arrossegar · Shift + WASD accelera',
                     hint: { look: 'Cursor a la vora per mirar', move: 'WASD per moure’t' } },
                D: { name: 'Híbrid',       mouse: 'Arrossega o cursor per mirar, WASD per moure’t', touch: '',
                     note: 'Clàssic lite + Cursor alhora · Shift + WASD accelera',
                     hint: { look: 'Arrossega o cursor a la vora per mirar', move: 'WASD o scroll per moure’t' } },
                E: { name: 'Gamer',        mouse: 'Clic i ratolí per mirar, WASD per moure’t', touch: '',
                     note: 'Esc retorna el cursor · Shift + WASD accelera',
                     hint: { look: 'Fes clic i mou el ratolí per mirar', move: 'WASD per moure’t' } }
            },
            orbit: {
                A: { name: 'Clàssic', mouse: 'Arrossega per girar, scroll per apropar', touch: 'Llisca per girar, pessiga per apropar' },
                B: { name: 'WASD',    mouse: 'WASD per mirar, scroll per apropar', touch: '',
                     note: 'A/D giren, W/S inclinen · arrossegar segueix funcionant',
                     hint: { look: 'WASD per mirar', move: 'Scroll o pessiga per apropar' } },
                C: { name: 'Cursor',  mouse: 'Cursor a la vora per girar, scroll per apropar', touch: '',
                     note: 'Sense arrossegar — els marges dirigeixen',
                     hint: { look: 'Cursor a la vora per girar', move: 'Scroll o pessiga per apropar' } },
                D: { name: 'Cursor i tecles', mouse: 'Cursor a la vora per girar, W/S per apropar', touch: '',
                     note: 'Sense arrossegar ni scroll · W apropa, S allunya',
                     hint: { look: 'Cursor a la vora per girar', move: 'W/S per apropar' } },
                E: { name: 'Espai',   mouse: 'WASD per mirar, Espai per apropar', touch: '',
                     note: 'Mantén Espai per fer zoom · cada pulsació inverteix · arrossegar segueix funcionant',
                     hint: { look: 'WASD per mirar', move: 'Mantén Espai per fer zoom — prémer inverteix' } },
                F: { name: 'Scroll',  mouse: 'Scroll per mirar, pessiga o W/S per apropar', touch: '',
                     note: 'Dos dits dirigeixen · arrossegar segueix funcionant',
                     hint: { look: 'Scroll per mirar', move: 'Pessiga o W/S per apropar' } },
                G: { name: 'Gamer',   mouse: 'Clic i ratolí per girar, W/S per apropar', touch: '',
                     note: 'Esc retorna el cursor',
                     hint: { look: 'Fes clic i mou el ratolí per girar', move: 'W/S per apropar' } }
            },
            'walk-touch': {
                A: { name: 'Clàssic',     mouse: '', touch: 'Llisca per mirar, joystick per moure’t' },
                B: { name: 'Giroscopi',   mouse: '', touch: 'Gira el dispositiu per mirar, joystick per moure’t',
                     note: 'Permet l’accés al moviment quan es demani · doble toc per recentrar',
                     hint: { look: 'Gira el dispositiu per mirar', move: 'Joystick per moure’t' } },
                C: { name: 'Mantenir',    mouse: '', touch: 'Llisca per mirar, mantén per caminar',
                     note: 'Sense joystick · mantén el dit a qualsevol lloc per avançar',
                     hint: { look: 'Llisca per mirar', move: 'Mantén el dit per caminar' } },
                D: { name: 'Dos dits',    mouse: '', touch: 'Llisca per mirar, dos dits per moure’t',
                     note: 'Llisca dos dits amunt per avançar, avall per recular',
                     hint: { look: 'Llisca per mirar', move: 'Dos dits amunt o avall per moure’t' } },
                E: { name: 'Polzes',      mouse: '', touch: 'Polze esquerre mou, polze dret mira',
                     note: 'El joystick apareix on posis el polze esquerre',
                     hint: { look: 'Meitat dreta: llisca per mirar', move: 'Meitat esquerra: arrossega per moure’t' } },
                F: { name: 'Gir + mantenir', mouse: '', touch: 'Gira el dispositiu per mirar, mantén per caminar',
                     note: 'Amb una mà · permet l’accés al moviment quan es demani · doble toc per recentrar',
                     hint: { look: 'Gira el dispositiu per mirar', move: 'Mantén el dit per caminar' } },
                G: { name: 'Mantén la vora', mouse: '', touch: 'Mantén la vora per mirar, joystick per moure’t',
                     note: 'Els costats giren, dalt i baix inclinen · sense lliscar',
                     hint: { look: 'Mantén un dit a la vora per mirar', move: 'Joystick per moure’t' } }
            },
            'orbit-touch': {
                A: { name: 'Clàssic',       mouse: '', touch: 'Llisca per girar, pessiga per apropar' },
                B: { name: 'Giroscopi',     mouse: '', touch: 'Gira el dispositiu per orbitar, pessiga per apropar',
                     note: 'Com sostenir una maqueta · permet l’accés al moviment quan es demani · doble toc per recentrar',
                     hint: { look: 'Gira el dispositiu per orbitar', move: 'Pessiga per apropar' } },
                C: { name: 'Mantén la vora', mouse: '', touch: 'Mantén la vora per orbitar, pessiga per apropar',
                     note: 'Recolza un dit al marge · lliscar segueix girant',
                     hint: { look: 'Mantén un dit a la vora per orbitar', move: 'Pessiga per apropar' } }
            }
        }
    }
};

// ── The seam ──────────────────────────────────────────────────────────────
/**
 * Everything a scheme changes about the controls goes through here — one
 * function, so the four behaviours can be built, swapped and thrown away
 * without touching mount.ts again.
 *
 * FOUR REGISTRIES, NOT ONE
 * ------------------------
 * A/B/C/D is not a single list. It branches twice:
 *
 *                    desktop            mobile
 *     walkable    A/B/C/D  (#1)      A/B/C/D  (#3)
 *     rotating    A/B/C/D  (#2)      A/B/C/D  (#4)
 *
 * A walkable scene and a rotating one have nothing in common to test — "click
 * where you want to stand" is meaningless without a floor — so Cartoixa gets
 * its own four candidates, not a degraded version of the gallery's. Same for
 * touch: a joystick scheme and a scroll scheme are not the same experiment.
 *
 * Both branches already exist in code and neither needs inventing:
 *   - walkable vs rotating  →  `ctx.kind`, which mount.ts derives from whether
 *     the scene carries a walk boundary of >= 3 points.
 *   - desktop vs mobile     →  `isTouch` in scheme-picker.ts (`pointer: coarse`).
 *
 * All four are live: #1 walk desktop A–E, #2 orbit desktop A–G, #3 walk touch
 * A–F, #4 orbit touch A–E. Live scenes:
 *     Galeria   navigation "free",     6-pt boundary, eyeHeight 2.6  -> walk
 *     Comedor   navigation "free",     5-pt boundary, eyeHeight 1.8  -> walk
 *     Cartoixa  navigation "rotating", no walk block                 -> orbit
 *
 * A is Classic: the shipped controls, untouched, the control arm.
 * B is Classic lite: A minus the two things people trip over — the joystick
 * and the Matterport-style click-to-move disc — plus a Shift boost so there is
 * still a way to cross a room quickly.
 * C is Cursor: the legacy viewer's hover-look (ExploreB) — the cursor resting
 * in a screen margin turns the view that way, faster toward the edge; no drag,
 * no click. WASD + Shift and the wheel as in B.
 * D is Hybrid: B and C at once — drag-look AND hover-look, hover yielding to a
 * drag (it re-arms only once the cursor has left the margins).
 * E is Gamer: pointer-lock mouse-look — a click captures the mouse, raw motion
 * turns the view, Esc releases; no drag, no click, no hover.
 *
 * Every branch sets EVERY capability rather than only the ones it changes: the
 * cog can switch schemes mid-session, so B -> A has to put the joystick and the
 * disc back, not just stop removing them.
 */
export interface SchemeContext {
    /** The controller mount() actually built for THIS scene. */
    nav: WalkController | OrbitController;
    /** Which one it is — with `touch`, which of the four registries applies. */
    kind: 'walk' | 'orbit';
    touch: boolean;
    canvas: HTMLCanvasElement;
}

/** Shift + WASD speed multiplier. The first cut was 1.1 and tested as "does
 *  nothing" on a real Mac — a 10% change hides under the velocity lerp.
 *  Double is unmistakable; tune down from here, not up from 1.1. */
const SHIFT_BOOST = 2;
/** Wheel dolly scale. A Mac trackpad delivers ~10x the delta of a notched
 *  wheel per gesture, plus momentum, so the shipped 1.0 flings you across the
 *  room. A third keeps a mouse notch usable (~0.35 m per notch). */
const WHEEL_SENSITIVITY = 0.35;
/** Scheme C's hover-look. Lenny's numbers where he gave them, the legacy
 *  viewer's (webpages/viewer/src/controls/ExploreB.ts) where he didn't. Every
 *  one is a dial, to be iterated by feel. */
const EDGE_LOOK: EdgeLookConfig = {
    margin: 0.25,                  // "25% of the screen, more or less"
    maxYaw: 1.05,                  // legacy MAX_YAW_SPEED — ~60°/s at the very edge
    maxPitch: 0.7,                 // legacy MAX_PITCH_SPEED — ~40°/s; 0 = yaw only
    ease: 3,                       // legacy EASING_POWER — a drift until deep into the margin
    floor: 0.15,                   // DC offset: +15% of peak the moment you cross into a margin (Lenny: "a notch up, flat")
    suppressSpeed: 1800,           // px/s — legacy 30 px/frame; a flick to a button turns nothing
    uiDeadZone: { w: 210, h: 70 }  // top-right: toolbar + cog, and the approach to them
};
/** Scheme E's mouse-look. Raw motion, so this is lower than the drag's
 *  0.0035 rad/px: a full turn is ~1.6 screen-widths of mouse travel, which is
 *  in the range PC games default to. */
const POINTER_LOOK: PointerLookConfig = { sens: 0.0022 };
/** Orbit schemes B/E keyboard steering, rad/s at full press. ~50°/s yaw is a
 *  full lap of Cartoixa in ~7 s; pitch slower because the elevation band is
 *  narrow. Both are dials, to be iterated by feel. */
const KEY_ORBIT: KeyOrbitConfig = { yawSpeed: 0.9, pitchSpeed: 0.6 };
/** Orbit schemes C/D margin steering — the walk EDGE_LOOK margins with B's
 *  key speeds as the peaks, so the screen edge ≡ holding the key (Lenny:
 *  "being in the right margin should be equivalent to pressing D in B"). */
const EDGE_ORBIT = {
    margin: 0.25,
    maxYaw: KEY_ORBIT.yawSpeed,
    maxPitch: KEY_ORBIT.pitchSpeed,
    ease: 3,
    floor: 0.15,
    suppressSpeed: 1800,
    uiDeadZone: { w: 210, h: 70 }
};
/** Held zoom for orbit D (W/S) and E (spacebar): ×2 distance per second held
 *  zooming out, ÷2 zooming in. A dial. */
const ZOOM_HOLD: ZoomHoldConfig = { rate: 2 };
/** Orbit F's scroll steering, rad per scroll px — near the drag's 0.003
 *  rad/px so the same hand motion covers similar ground; pitch lower because
 *  the elevation band is narrow. Signs live in the controller. Dials. */
const SCROLL_ORBIT: ScrollOrbitConfig = { yawPerPx: 0.0025, pitchPerPx: 0.0018 };
/** Orbit G's captured-mouse steering, rad per counted px — a touch under the
 *  drag's 0.003 because raw motion has no button to anchor the hand. */
const POINTER_ORBIT: PointerOrbitConfig = { sens: 0.0025 };
// ── Mobile dials (registries #3/#4) ──
/** Device-orientation look/orbit: one-to-one with the device. */
const GYRO_LOOK: GyroLookConfig = { yawGain: 1, pitchGain: 1 };
const GYRO_ORBIT: GyroOrbitConfig = { yawGain: 1, pitchGain: 1 };
/** Walk G's hold-the-edge: desktop C's margins and peaks — sides turn,
 *  top/bottom tilt, no swipe (Lenny: "the 25% margin of the right or left to
 *  turn, kind of like the desktop version"; then "scrolling should no longer
 *  work, and the top/bottom margin should pan up and down"). A gentler curve
 *  than the cursor's cubic — a thumb lands where it lands, it can't creep
 *  deeper by a pixel. Dials. */
const EDGE_HOLD: EdgeLookConfig = { ...EDGE_LOOK, ease: 2, floor: 0.2 };
/** Two-finger dolly: a ~300 px slide ≈ 1 m at this scale (accumulates
 *  through the wheel's velocity/decay). */
const TWO_FINGER: TwoFingerMoveConfig = { sens: 0.0035 };

export function applyScheme(id: SchemeId, ctx: SchemeContext): void {
    // Exposed for devtools and for the analytics hook that will report which
    // scheme a session actually used.
    (window as unknown as Record<string, unknown>).__scheme = id;
    const registry = registryFor(ctx.kind, ctx.touch);

    // Every scheme starts from the SHIPPED controller — all setters back to
    // their defaults — and then flips only what it changes. The cog can
    // switch any scheme to any other mid-session, so this is what guarantees
    // E -> A releases the pointer lock and puts the joystick and disc back.
    if (ctx.kind === 'walk') {
        const walk = ctx.nav as WalkController;
        walk.setJoystickEnabled(true);
        walk.setJoystickMode('fixed');
        walk.setClickToMoveEnabled(true);
        walk.setDragLookEnabled(true);
        walk.setEdgeLook(null);
        walk.setPointerLook(null);
        walk.setShiftBoost(1);
        walk.setWheelSensitivity(1);
        walk.setGyroLook(null);
        walk.setHoldToWalk(false);
        walk.setTwoFingerMove(null);
        walk.setEdgeHold(null);

        if (registry === 'walk') {
            // #1 — desktop. B–E all drop the joystick and the disc and gain
            // Shift + a calmer wheel; they differ in how you LOOK.
            if (id !== 'A') {
                walk.setJoystickEnabled(false);
                walk.setClickToMoveEnabled(false);
                walk.setShiftBoost(SHIFT_BOOST);
                walk.setWheelSensitivity(WHEEL_SENSITIVITY);
            }
            switch (id) {
                case 'C': walk.setDragLookEnabled(false); walk.setEdgeLook(EDGE_LOOK); break;
                case 'D': walk.setEdgeLook(EDGE_LOOK); break;
                case 'E': walk.setDragLookEnabled(false); walk.setPointerLook(POINTER_LOOK); break;
                default: break;   // A (shipped), B (the drops above)
            }
        } else {
            // #3 — touch. Swipe-look stays in every scheme; B–F drop the
            // tap-to-move disc so a stray tap never teleports the tester.
            if (id !== 'A') walk.setClickToMoveEnabled(false);
            switch (id) {
                case 'B': walk.setGyroLook(GYRO_LOOK); break;
                case 'C': walk.setJoystickEnabled(false); walk.setHoldToWalk(true); break;
                case 'D': walk.setJoystickEnabled(false); walk.setTwoFingerMove(TWO_FINGER); break;
                case 'E': walk.setJoystickMode('floating'); break;
                case 'F': walk.setJoystickEnabled(false); walk.setGyroLook(GYRO_LOOK); walk.setHoldToWalk(true); break;
                case 'G': walk.setDragLookEnabled(false); walk.setEdgeHold(EDGE_HOLD); break;
                default: break;   // A (shipped)
            }
        }
        return;
    }

    const orbit = ctx.nav as OrbitController;
    orbit.setDragRotateEnabled(true);
    orbit.setKeyOrbit(null);
    orbit.setEdgeOrbit(null);
    orbit.setWheelZoomEnabled(true);
    orbit.setKeyZoom(null);
    orbit.setSpaceZoom(null);
    orbit.setScrollOrbit(null);
    orbit.setPointerOrbit(null);
    orbit.setGyroOrbit(null);
    orbit.setEdgeHold(null);

    if (registry === 'orbit') {
        // #2 — desktop.
        //   B: A + WASD/arrow steering.
        //   C: margin steering (right margin ≡ holding D); no drag.
        //   D: C, zoom by W/S instead of the wheel.
        //   E: B, zoom by the spacebar (press flips, hold zooms) instead of the wheel.
        //   F: scroll steers; pinch (ctrlKey wheel) or W/S zooms.
        //   G: pointer lock steers; W/S zooms.
        switch (id) {
            case 'B': orbit.setKeyOrbit(KEY_ORBIT); break;
            case 'C': orbit.setDragRotateEnabled(false); orbit.setEdgeOrbit(EDGE_ORBIT); break;
            case 'D': orbit.setDragRotateEnabled(false); orbit.setEdgeOrbit(EDGE_ORBIT);
                      orbit.setWheelZoomEnabled(false); orbit.setKeyZoom(ZOOM_HOLD); break;
            case 'E': orbit.setKeyOrbit(KEY_ORBIT); orbit.setWheelZoomEnabled(false); orbit.setSpaceZoom(ZOOM_HOLD); break;
            case 'F': orbit.setKeyZoom(ZOOM_HOLD); orbit.setScrollOrbit(SCROLL_ORBIT); break;
            case 'G': orbit.setDragRotateEnabled(false); orbit.setWheelZoomEnabled(false);
                      orbit.setKeyZoom(ZOOM_HOLD); orbit.setPointerOrbit(POINTER_ORBIT); break;
            default: break;   // A
        }
    } else {
        // #4 — touch. Swipe + pinch stay in every scheme.
        switch (id) {
            case 'B': orbit.setGyroOrbit(GYRO_ORBIT); break;
            case 'C': orbit.setEdgeHold(EDGE_ORBIT); break;
            default: break;   // A
        }
    }
}

// ── Tutorial rows ─────────────────────────────────────────────────────────
// The shipped card teaches from a mode × device matrix that has no cell for
// "hover to look" or "the mouse is captured" — so a scheme can hand it rows of
// its own. The glyphs reuse the card's animation classes (ch-anim-*,
// viewer.css) so they move like family.
const hintSvg = (d: string) =>
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const HINT_GLYPH = {
    // A cursor sliding toward the screen edge (the bar).
    edgeLook: hintSvg('<path d="M20.5 4.5v15"/><g class="ch-anim-slide"><path d="M5.5 4l5.5 13.5 1.9-5.4 5.3-2z"/></g>'),
    // A crosshair drifting with the mouse — no cursor, the view follows.
    lock: hintSvg('<g class="ch-anim-slide"><circle cx="12" cy="12" r="5.5"/><path d="M12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3"/></g>'),
    // A WASD cluster; the top key bobs like it is being pressed.
    wasd: hintSvg('<g class="ch-anim-scroll"><rect x="9" y="3" width="6" height="6" rx="1.3"/></g><rect x="2.5" y="11.5" width="6" height="6" rx="1.3"/><rect x="9" y="11.5" width="6" height="6" rx="1.3"/><rect x="15.5" y="11.5" width="6" height="6" rx="1.3"/>'),
    // The shipped card's zoom magnifier (controls-hint GLYPH.zoom), so orbit
    // B's zoom row looks exactly like orbit A's.
    zoom: hintSvg('<g class="ch-anim-pulse"><circle cx="10.5" cy="10.5" r="6.2"/><path d="M15.2 15.2L20 20"/><path d="M8 10.5h5"/><path d="M10.5 8v5"/></g>'),
    // W and S stacked — orbit D's zoom keys, nothing else in the cluster.
    wsKeys: hintSvg('<g class="ch-anim-scroll"><rect x="9" y="3" width="6" height="6" rx="1.3"/></g><rect x="9" y="12.5" width="6" height="6" rx="1.3"/>'),
    // A spacebar with an in/out arrow above it — orbit E's zoom.
    space: hintSvg('<rect x="4" y="15" width="16" height="5.5" rx="1.6"/><g class="ch-anim-scroll"><path d="M12 3.5v8"/><path d="M9.8 5.7L12 3.5l2.2 2.2"/><path d="M9.8 9.3l2.2 2.2 2.2-2.2"/></g>'),
    // The shipped card's scroll chevrons — orbit F steers with them.
    scroll: hintSvg('<g class="ch-anim-scroll"><path d="M8 8l4-4 4 4"/><path d="M8 16l4 4 4-4"/></g>'),
    // The shipped card's pinch corners — orbit F's zoom.
    pinch: hintSvg('<g class="ch-anim-pinch-a"><path d="M9.5 9.5L4.5 4.5"/><path d="M4.5 9V4.5H9"/></g><g class="ch-anim-pinch-b"><path d="M14.5 14.5l5 5"/><path d="M19.5 15v4.5H15"/></g>'),
    // ── Touch (registries #3/#4). swipe/joystick are the shipped card's. ──
    swipe: hintSvg('<g class="ch-anim-slide"><circle cx="6.5" cy="12" r="2.3" fill="currentColor" stroke="none"/><path d="M11 12h7"/><path d="M14.8 8.4l3.7 3.6-3.7 3.6"/></g>'),
    joystick: hintSvg('<circle cx="12" cy="12" r="8.5"/><g class="ch-anim-orbit"><circle cx="12" cy="9.6" r="2.7" fill="currentColor" stroke="none"/></g>'),
    // A phone rocking between motion arcs.
    gyro: hintSvg('<g class="ch-anim-rock"><rect x="7" y="3" width="10" height="18" rx="2"/></g><path d="M3.5 8.5a9 9 0 0 0 0 7"/><path d="M20.5 8.5a9 9 0 0 1 0 7"/>'),
    // A fingertip with a ring pulsing around it — held down.
    hold: hintSvg('<circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none"/><g class="ch-anim-pulse"><circle cx="12" cy="12" r="7"/></g>'),
    // Two fingertips riding the scroll chevrons.
    twoFinger: hintSvg('<g class="ch-anim-scroll"><circle cx="9" cy="12" r="2.2" fill="currentColor" stroke="none"/><circle cx="15" cy="12" r="2.2" fill="currentColor" stroke="none"/></g><path d="M9 5l3-3 3 3"/><path d="M9 19l3 3 3-3"/>'),
    // A fingertip with two rings — tap, tap.
    // Two fingertips on an arc with the rotate arrow.
    // A fingertip sliding to the screen edge (the bar).
    fingerEdge: hintSvg('<path d="M20.5 4.5v15"/><g class="ch-anim-slide"><circle cx="9" cy="12" r="2.6" fill="currentColor" stroke="none"/></g>')
};
type HintGlyph = keyof typeof HINT_GLYPH;

/** Which glyph fronts each scheme's [look, move] row, per registry. Schemes
 *  absent here (the A's, walk B) use the shipped card. */
const HINT_GLYPHS: Record<Registry, Partial<Record<SchemeId, [HintGlyph, HintGlyph]>>> = {
    walk: {
        C: ['edgeLook', 'wasd'],
        D: ['edgeLook', 'wasd'],
        E: ['lock', 'wasd']
    },
    orbit: {
        B: ['wasd', 'zoom'],
        C: ['edgeLook', 'zoom'],
        D: ['edgeLook', 'wsKeys'],
        E: ['wasd', 'space'],
        F: ['scroll', 'pinch'],
        G: ['lock', 'wsKeys']
    },
    'walk-touch': {
        B: ['gyro', 'joystick'],
        C: ['swipe', 'hold'],
        D: ['swipe', 'twoFinger'],
        E: ['swipe', 'joystick'],
        F: ['gyro', 'hold'],
        G: ['fingerEdge', 'joystick']
    },
    'orbit-touch': {
        B: ['gyro', 'pinch'],
        C: ['fingerEdge', 'pinch']
    }
};

/**
 * Rows for the tutorial card after picking `id`, or undefined to let the card
 * teach what it always has (the A's, and walk B).
 */
export function schemeHintRows(id: SchemeId, registry: Registry): HintRow[] | undefined {
    const hint = COPY[resolveLang()].schemes[registry][id]?.hint;
    const glyphs = HINT_GLYPHS[registry][id];
    if (!hint || !glyphs) return undefined;
    return [
        { glyph: HINT_GLYPH[glyphs[0]], label: hint.look, probe: 'look' },
        { glyph: HINT_GLYPH[glyphs[1]], label: hint.move, probe: 'move' }
    ];
}
